import { ReactElement, useMemo } from 'react';
import styles from 'platform/components/menu-page/MenuPage.module.scss';
import { MenuCartItem } from '../../models/menu.types';
import { useMenuWithCart } from '../../contexts/MenuWithCart';
import ProductCards from './ProductCards';

type Props = {
  activeFilterLabel?: string;
  menuItems: MenuCartItem[];
  isSkipped: boolean;
  animateTiles: boolean;
  isActive: boolean;
  isLoggedIn?: boolean;
  onAddToCart: (menuItem: MenuCartItem) => void;
  onUpdateCartQuantity: (cartItem: MenuCartItem, quantity: number) => void;
  handleSoldOutLtoClick: () => void;
};

export function MerchlessProductCards({
  activeFilterLabel,
  menuItems,
  isSkipped,
  animateTiles,
  isActive,
  isLoggedIn,
  onAddToCart,
  onUpdateCartQuantity,
  handleSoldOutLtoClick,
}: Props): ReactElement {
  const { menu } = useMenuWithCart();

  // Flatten all items into a single list while preserving merchandising order.
  // When a filter is active, merch group headers/separators are hidden but we
  // still want items to appear in the same sequence as the grouped view:
  //   1. Items belonging to merch groups, ordered by group.displayOrder then mgmi.displayOrder
  //   2. Ungrouped items ordered by item.displayOrder
  const sortedItems = useMemo(() => {
    // Sort groups by their display order so the flattened sequence matches the grouped view
    const sortedGroups = [...(menu?.merchandisingGroups ?? [])]
      .sort((a, b) => a.displayOrder - b.displayOrder);

    const menuItemById = new Map(menuItems.map((mi) => [mi.id, mi]));
    const seen = new Set<number>();
    const ordered: MenuCartItem[] = [];

    // Sort each group in order, adding its items sorted by their within-group displayOrder.
    // Only items present in the filtered menuItems list are included.
    sortedGroups.forEach((group) => {
      [...group.merchandisingGroupMenuItems]
        .sort((a, b) => a.displayOrder - b.displayOrder)
        .forEach((mgmi) => {
          const mi = menuItemById.get(mgmi.menuItemId);
          if (mi) {
            ordered.push(mi);
            seen.add(mi.id);
          }
        });
    });

    // Append any filtered items that don't belong to any merch group
    const ungrouped = menuItems
      .filter((mi) => !seen.has(mi.id))
      .sort((a, b) => (a.displayOrder ?? Infinity) - (b.displayOrder ?? Infinity));

    return [...ordered, ...ungrouped];
  }, [menuItems, menu?.merchandisingGroups]);

  return (
    <div
      className={styles.merch_group_container}
    >
      <ProductCards
        menuItemArr={sortedItems}
        isSkipped={isSkipped}
        animateTiles={animateTiles}
        isActive={isActive}
        isLoggedIn={isLoggedIn}
        leftAligned
        onAddToCart={onAddToCart}
        onUpdateCartQuantity={onUpdateCartQuantity}
        header={<div className={styles.merch_group_separator_text}>{activeFilterLabel}</div>}
        handleSoldOutLtoClick={handleSoldOutLtoClick}
        data-heap-merch-group-title="Not Assigned"
      />
    </div>
  );
}
